// @flow

import React, { Fragment, useState, useContext } from 'react';
import moment from 'moment';

import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { themes } from 'Components/widgets/alternative-proposals/alternative-price/AlternativePrice.themes';
import { pluralizeLabel } from 'Utils/label/labelUtils';
import { COLORS } from 'Components/utils/color/colors';
import { Price } from 'Components/frameworks/price/Price';
import { ResortBanner } from 'Components/widgets/resort-banner/ResortBanner';
import { Collapse } from 'react-collapse';
import classnames from 'classnames';
import { StripButton } from 'Components/frameworks/strip-button/StripButton';
import { ProductService } from 'Components/widgets/product-services/product-service/ProductService';
import { Button } from 'Components/frameworks/button/Button';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { AlternativePriceCriteriaButton } from 'Components/widgets/alternative-proposals/alternative-price-criteria-button/AlternativePriceCriteriaButton';
import { OPEN_CRITERIA_WHEN_EVENT } from 'Components/widgets/criteria/criteria-when/constants';
import { OPEN_CRITERIA_WHO_EVENT } from 'Components/widgets/criteria/criteria-who/constants';
import { OPEN_CRITERIA_TRANSPORT_EVENT } from 'Components/widgets/criteria/criteria-transport/constants';
import { DEFAULT_DEPARTURE_CITY } from 'Store/trip-criteria/departure-city/constants';
import { BOOKING_ENGINE_ENTRIES } from 'Store/booking/constants';
import { t } from 'Src/i18n';
import {
  ALL_INCLUSIVE_SERVICE_LIST_DESKTOP_OVERFLOW_THRESHOLD,
  ALL_INCLUSIVE_SERVICE_LIST_MOBILE_OVERFLOW_THRESHOLD,
} from 'Components/widgets/alternative-proposals/alternative-price/constants';

type Props = {|
  proposals: $ReadOnlyArray<Proposal>,
  resort: Resort,
  searchCriteria: ProposalSearchCriteria,
  departureCities: $ReadOnlyArray<{| name: string, value: string |}>,
  seeOtherProposalsCallback: () => void,
  onModify?: () => void,
  bookingEngineEntryPoint?: string,
  continueToBookingEngine: () => Promise<*>,
  theme?: string,
  hasOtherProposals: boolean,
|};

export const AlternativePrice = ({
  proposals,
  resort,
  searchCriteria: { arrivalDate, duration, adultsCount, kidsCount, departureCity },
  departureCities,
  seeOtherProposalsCallback,
  onModify,
  bookingEngineEntryPoint,
  continueToBookingEngine,
  theme = 'default',
  hasOtherProposals,
}: Props) => {
  const { isDesktop } = useContext(ResponsiveContext);

  const [isDetailOpened, setIsDetailOpened] = useState(false);

  const { price, allInclusiveServices } = proposals[0];

  if (!price) {
    return null;
  }

  const { thRoot, thSubmitButton } = themes[theme];

  const noTransport = departureCity === DEFAULT_DEPARTURE_CITY;

  const openCriteriaWhenEvent = document.createEvent('Event');
  openCriteriaWhenEvent.initEvent(OPEN_CRITERIA_WHEN_EVENT, true, true);
  const openCriteriaWhoEvent = document.createEvent('Event');
  openCriteriaWhoEvent.initEvent(OPEN_CRITERIA_WHO_EVENT, true, true);
  const openCriteriaTransportEvent = document.createEvent('Event');
  openCriteriaTransportEvent.initEvent(OPEN_CRITERIA_TRANSPORT_EVENT, true, true);

  const criteriaClickHandlerFactory = event => () => {
    if (onModify) {
      onModify();
    }

    if (bookingEngineEntryPoint === BOOKING_ENGINE_ENTRIES.PRICE_REMOTE) {
      setTimeout(() => {
        document.dispatchEvent(event);
      }, 200);
    }
  };

  const firstDay = moment(arrivalDate);
  const city = departureCities.find(d => departureCity === d.value);

  const { bestPrice, total, currency } = price;

  const overflowThreshold = isDesktop
    ? ALL_INCLUSIVE_SERVICE_LIST_DESKTOP_OVERFLOW_THRESHOLD
    : ALL_INCLUSIVE_SERVICE_LIST_MOBILE_OVERFLOW_THRESHOLD;

  return (
    <Fragment>
      {hasOtherProposals ? (
        <Button
          color={isDesktop ? COLORS.WHITE : COLORS.TURQUOISE}
          colorBg={isDesktop ? COLORS.TURQUOISE : COLORS.GRAY_LIGHTER}
          colorBorder={isDesktop ? COLORS.TURQUOISE : COLORS.GRAY_LIGHTER}
          className="lg:mt-8 alternative-price-other-proposals-btn"
          onClick={seeOtherProposalsCallback}
          trackingData={{
            categoryGA: 'resort',
            actionGA: 'checkPriceKO',
            labelGA: 'seeOtherProposals',
          }}
        >
          {t('booking.counterProposalPopIn.seeOtherProposalsButton')}
        </Button>
      ) : null}
      <div className={classnames('alternative-price-fake-remote-container', thRoot)}>
        <div className="p-3  flex flex-col items-center">
          <div className="flex justify-between w-full">
            <ResortBanner product={resort} />
            <div className="text-center w-full lg:w-auto lg:text-right lg:flex lg:items-center">
              <div className="hidden lg:flex lg:items-center">
                <Icon color={COLORS.BLUE} name={ICONS.ALL_INCLUSIVE} width="1.5rem" />
                <div className="font-brand text-blue text-md ml-2 rtl:ml-2">
                  {t('booking.priceDetails.packagePrice.allInclusive')}
                </div>
              </div>
              <Price
                bestPrice={bestPrice}
                currency={currency}
                initialPrice={total}
                theme="remote"
                unit={t('booking.priceDetails.packagePrice.totalPrice')}
                className="inline-flex"
                withInstalment={false}
              />
            </div>
          </div>
          <div className="search-criteria-container flex mt-2 lg:mt-3 w-full flex-between flex-wrap lg:flex-no-wrap">
            <AlternativePriceCriteriaButton
              iconName={ICONS.DATE}
              onClick={onModify ? criteriaClickHandlerFactory(openCriteriaWhenEvent) : undefined}
            >
              {`${firstDay.format('DD MMM')} - ${firstDay.add(duration, 'd').format('DD MMM')}`}
            </AlternativePriceCriteriaButton>
            <AlternativePriceCriteriaButton
              iconName={ICONS.USERS}
              onClick={onModify ? criteriaClickHandlerFactory(openCriteriaWhoEvent) : undefined}
            >
              {`${adultsCount} ${pluralizeLabel(
                t('booking.common.adult'),
                t('booking.common.adults'),
                adultsCount,
              )} ${
                kidsCount > 0
                  ? ` - ${kidsCount} ${pluralizeLabel(
                      t('booking.common.child'),
                      t('booking.common.children'),
                      kidsCount,
                    )}`
                  : ''
              }`}
            </AlternativePriceCriteriaButton>
            <AlternativePriceCriteriaButton
              iconName={noTransport ? ICONS.NO_TRANSPORT : ICONS.TRANSPORT}
              onClick={
                onModify ? criteriaClickHandlerFactory(openCriteriaTransportEvent) : undefined
              }
            >
              {city ? city.name : ''}
            </AlternativePriceCriteriaButton>
            <Button
              style={{ minWidth: 80, width: isDesktop ? 250 : '100%' }}
              className={thSubmitButton}
              type="submit"
              trackingData={{
                categoryGA: 'resort',
                actionGA: 'checkPriceKO',
                labelGA: 'rate_check',
              }}
              colorBg={COLORS.SUCCESS}
              colorBorder={COLORS.SUCCESS}
              onClick={() => {
                continueToBookingEngine();
              }}
            >
              {t('common.continue')}
            </Button>
          </div>
          {onModify && (
            <Button
              iconLeft={{ name: ICONS.PEN }}
              color={COLORS.TURQUOISE}
              colorBg={COLORS.WHITE}
              className="text-md underline"
              trackingData={{
                categoryGA: 'resort',
                actionGA: 'checkPriceKO',
                labelGA: 'rate_modify',
              }}
              onClick={() => {
                onModify();
              }}
            >
              {t('common.modify')}
            </Button>
          )}
          <div className="w-full px-6">
            <div className="flex flex-wrap proposal-services-container">
              {allInclusiveServices.slice(0, overflowThreshold).map(service => (
                <ProductService service={service} className="w-full lg:w-1/2" key={service.label} />
              ))}
            </div>
            {allInclusiveServices.length > overflowThreshold && (
              <React.Fragment>
                <Collapse isOpened={isDetailOpened}>
                  <div className="flex flex-wrap proposal-services-container">
                    {allInclusiveServices
                      .slice(overflowThreshold, allInclusiveServices.length)
                      .map(service => (
                        <ProductService
                          service={service}
                          className="w-full lg:w-1/2"
                          key={service.label}
                        />
                      ))}
                  </div>
                </Collapse>
                {!isDetailOpened ? (
                  <div className="proposal-services-container__overlay w-full" />
                ) : null}
              </React.Fragment>
            )}
          </div>
        </div>
        {allInclusiveServices.length > overflowThreshold && (
          <div className="flex items-center justify-center absolute left-1/2 -translate-x-50">
            <StripButton
              className="font-bold text-center text-blue text-sm underline"
              height={30}
              onClick={() => setIsDetailOpened(!isDetailOpened)}
              orientation="bottom"
            >
              <div className="flex items-center text-base">
                {!isDetailOpened
                  ? t('priceRemote.openAllInclusiveDetails')
                  : t('priceRemote.closeAllInclusiveDetails')}
                <Icon
                  className={classnames('text-blue rtl:ml-2 transition-transform', {
                    ['rotate-180']: isDetailOpened,
                  })}
                  name={ICONS.BOLDER_ARROW_FORTH_DOWN}
                  width="0.625rem"
                />
              </div>
            </StripButton>
          </div>
        )}
      </div>
    </Fragment>
  );
};
